import { limeTick } from './saberes.ts';
import { ARTIFICER, CLERIC, FORKS, HEROES, PALADIN, GLYPH_FX, GLYPHS, SKILL_CASTS, SKILL_FX, glyphFits, glyphTurns, potency, SKILL_RANK_BP, SKILLS, VARIANT_PATCH, WORKSHOP, LAND_LEVELS, ZONE_DMG_TYPE, type DamageType, type FieldPatch, type ForkId, type GlyphId, type SkillDef, type SkillField, type SkillId } from '@somnhold/content';
import { runCast } from './cast.ts';
import { blockingAtCell, cache, cellAtWorld, enemySlideClear, heroWalkable, structureAtCell } from '../grid.ts';
import { DIRS, hexToWorld, worldToHex } from '../hex.ts';
import { cosA, dist2, iatan2, idiv, imax, isqrt, mulBp, sinA, withinRange } from '../math/fixed.ts';
import type { BattleState, Command, Enemy, Hero, Structure, Turret, Zone } from '../types.ts';
import { damageEnemy, damageHero, enemiesNear, ev, findHero, findStructure, healHero, histAdd, isFlying, pstats, structurePos, targetable, wardHero } from './combat.ts';
import { featZone } from './feats.ts';
import { bloomTick, formZone, hallowInked, rainKilled, rainTick, sanctuaryHero, sanctuaryTick, stormBlow, stormLeap, zoneEnded } from './forms.ts';
import { flareGround, flareOwner, trapOwner, trapShut, trapTakes } from './panoply-ranger.ts';
import { totemOwner, totemTick } from './panoply-cleric.ts';
import { setCastSlot } from './uniques.ts';
import { druidZone } from './panoply-druid.ts';

/** The unique forms' own ground (battle/forms.ts). */
const FORM_ZONES: ReadonlySet<Zone['kind']> = new Set<Zone['kind']>(['cleft', 'fireLine', 'toll', 'oldRoot', 'parapet', 'puddle', 'vigil', 'sprout']);
import { corrupt, furyBp, lifesteal } from './kits.ts';
import { ascuasZone } from './ascuas.ts';
import { obsidianZone } from './obsidian.ts';
import { omenZone } from './omens.ts';
import { blastZone } from './unsleeping/kit.ts';
import { slowFor, stunFor } from './towers.ts';
import { rngInt } from '../math/rng.ts';
import { skillLevelBp } from './create.ts';
import { artificerZone, minerBurst, mortarShell } from './panoply-artificer.ts';
import { cometMeteor, mageZone, orbTouched, orbTurn, riftOpen } from './panoply-mage.ts';

/** How long an overloaded clockwork turret rests: shorter with a workshop raised to level 3 on terraced land (LAND_LEVELS). */
const overloadRest = (s: BattleState): number => {
  if (!s.map.land) return WORKSHOP.overloadRest;
  let lv = 0;
  for (const st of s.structures) if (st.type === 'workshop' && !st.destroyed && st.level > lv) lv = st.level;
  return lv > 0 ? mulBp(WORKSHOP.overloadRest, LAND_LEVELS.workshop.overloadRestBp[lv - 1] ?? 10000) : WORKSHOP.overloadRest;
};

type SkillCmd = Extract<Command, { t: 'skill' }>;

/** The skills a hero carries, one per slot: its class's own unless a preparation picked others. */
export const kitOf = (h: Pick<Hero, 'cls' | 'kit'>): readonly SkillId[] => h.kit ?? HEROES[h.cls].skills;

export const skillOf = (h: Hero, slot: 0 | 1 | 2): SkillDef => SKILLS[kitOf(h)[slot] as SkillId];

/** A skill's damage or amount for this hero: its power (ghost, mercenary…) and, for a barbarian, its fury. */
/** What the hero brings to a building (a repair, a shield on a wall): its presence and fury, not its level (buildings don't grow with it). */
export const structural = (h: Hero, v: number): number => mulBp(mulBp(v, h.stats.powerBp), furyBp(h));
/** What the hero brings to anything but damage (a heal, a shield, a golem's health): its level, presence and fury. */
export const presence = (h: Hero, v: number): number => structural(h, mulBp(v, skillLevelBp(h.level)));
/** A skill's damage (and what it builds or calls up): presence, fury and the +damage of gear and tree. */
export const power = (h: Hero, v: number): number => {
  const d = mulBp(presence(h, v), 10000 + (h.stats.damageBp ?? 0) + (h.stats.skillBp ?? 0));
  return (h.roar ?? 0) > 0 ? mulBp(d, SKILL_FX.clanRoar.damageBp) : d;
};
/** A cleric's mend inspires the mended (CLERIC.inspire): quicker blows for a while. */
export const inspire = (o: Hero): void => {
  if ((o.inspire ?? 0) < CLERIC.inspire.ticks) o.inspire = CLERIC.inspire.ticks;
};
/** A heal's power: its presence, and the cleric's potency from the tree. */
export const healPower = (h: Hero, v: number): number => mulBp(presence(h, v), h.stats.healBp ?? 10000);

/** Nearest targetable enemy to a point within a radius. */
export const nearestEnemy = (s: BattleState, x: number, y: number, r: number, groundOnly = false): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of enemiesNear(s, x, y, r)) {
    if (!targetable(s, e)) continue;
    if (groundOnly && isFlying(e)) continue;
    const dx = e.x - x;
    const dy = e.y - y;
    const d = dx * dx + dy * dy;
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

/** Clamp a target point to the cast range around the hero. */
export const clampPoint = (h: Hero, x: number, y: number, range: number): { x: number; y: number } => {
  const dx = x - h.x;
  const dy = y - h.y;
  const d2 = dx * dx + dy * dy;
  if (d2 <= range * range) return { x, y };
  const d = isqrt(d2);
  return { x: h.x + idiv(dx * range, d), y: h.y + idiv(dy * range, d) };
};

/** Hex direction (index into DIRS) closest to a world angle in [0,1024). DIRS[k] points at −k·60°. */
const hexDirFor = (ang: number): number => idiv((((1024 - ang) % 1024) * 6 + 512), 1024) % 6;

/**
 * A skill as the tree leaves it: ranks II/III add 30 % per rank to damage, duration and amount (one more of a
 * count), and the variant may change its numbers (`VARIANT_PATCH`; the rest of what a variant does is in the
 * skill's program, `SKILL_CASTS`).
 */
export const rankedSkill = (base: SkillDef, r: number, fork: ForkId | null, glyph: GlyphId | null = null): SkillDef => {
  let sk = base;
  if (r > 1) {
    const bp = 10000 + (r - 1) * SKILL_RANK_BP;
    sk = {
      ...base,
      damage: mulBp(base.damage, bp),
      duration: mulBp(base.duration, bp),
      stun: mulBp(base.stun, bp),
      amount: SKILL_CASTS[base.id].count ? base.amount + (r - 1) : mulBp(base.amount, bp),
    };
  }
  const variant = fork ? VARIANT_PATCH[fork] : undefined;
  if (variant) sk = patched(sk, variant);
  // The glyph carved on its button (docs/GLIFOS.md), after the variant; one that doesn't fit does nothing.
  if (glyph && glyphFits(base.id, glyph)) sk = patched(sk, GLYPHS[glyph].patch(base.id));
  return sk;
};

/** A skill with some of its numbers changed: a multiplier (bp), a few more, or a number of its own. */
export const patched = (sk: SkillDef, patch: Partial<Record<SkillField, FieldPatch>>): SkillDef => {
  const out = { ...sk };
  for (const k in patch) {
    const f = k as SkillField;
    const p = patch[f] as FieldPatch;
    out[f] = 'bp' in p ? mulBp(sk[f], p.bp) : 'add' in p ? sk[f] + p.add : p.set;
  }
  return out;
};

const ranked = (h: Hero, slot: 0 | 1 | 2): SkillDef => rankedSkill(skillOf(h, slot), h.ranks?.[slot] ?? 1, h.forks?.[slot] ?? null, h.glyphs?.[slot] ?? null);

/** A poison on a nightmare (renewed, not stacked): `damage` every `every` ticks for `ticks`. */
export const poison = (e: Enemy, damage: number, every: number, ticks: number, pid: string, type: DamageType = 'poison'): void => {
  e.dotDmg = damage;
  e.dotType = type;
  e.dotEvery = every;
  e.dotTicks = ticks;
  e.lastPid = pid;
  e.wasps = undefined;
};

/** The targetable enemies within `width` of the segment from (x0, y0) to (x1, y1), from its start on (by id on ties). */
export const enemiesInLine = (s: BattleState, x0: number, y0: number, x1: number, y1: number, width: number): Enemy[] => {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const l2 = imax(1, dx * dx + dy * dy);
  const out: { e: Enemy; t: number }[] = [];
  for (const e of s.enemies) {
    if (!targetable(s, e)) continue;
    let t = (e.x - x0) * dx + (e.y - y0) * dy;
    t = t < 0 ? 0 : t > l2 ? l2 : t;
    const px = x0 + Math.trunc((dx * t) / l2);
    const py = y0 + Math.trunc((dy * t) / l2);
    if (withinRange(e.x, e.y, px, py, width)) out.push({ e, t });
  }
  return out.sort((a, b) => a.t - b.t || a.e.id - b.e.id).map((o) => o.e);
};

/** The heroes standing within `width` of the segment from (x0, y0) to (x1, y1). */
export const heroesInLine = (s: BattleState, x0: number, y0: number, x1: number, y1: number, width: number): Hero[] => {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const l2 = imax(1, dx * dx + dy * dy);
  return s.heroes.filter((o) => {
    if (o.downTicks > 0) return false;
    let t = (o.x - x0) * dx + (o.y - y0) * dy;
    t = t < 0 ? 0 : t > l2 ? l2 : t;
    return withinRange(o.x, o.y, x0 + Math.trunc((dx * t) / l2), y0 + Math.trunc((dy * t) / l2), width);
  });
};

/** The targetable enemies within `r` of a hero inside a cone `deg` wide pointing at (x, y). */
export const enemiesInCone = (s: BattleState, h: Hero, x: number, y: number, r: number, deg: number): Enemy[] => {
  const aim = iatan2(y - h.y, x - h.x);
  const half = idiv(deg * 1024, 720);
  return enemiesNear(s, h.x, h.y, r, (e) => {
    if (!targetable(s, e)) return false;
    let d = (iatan2(e.y - h.y, e.x - h.x) - aim) % 1024;
    if (d < 0) d += 1024;
    if (d > 512) d = 1024 - d;
    return d <= half || withinRange(e.x, e.y, h.x, h.y, 400);
  });
};

/** A point `len` beyond (x0, y0) toward (x1, y1). */
export const along = (x0: number, y0: number, x1: number, y1: number, len: number): { x: number; y: number } => {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const d = imax(1, isqrt(dx * dx + dy * dy));
  return { x: x0 + idiv(dx * len, d), y: y0 + idiv(dy * len, d) };
};

/** A druid's wasps on a nightmare: their poison runs on it (renewed, not stacked); `jump`, they fly on when it falls. */
export const sting = (e: Enemy, damage: number, every: number, ticks: number, pid: string, jump: boolean, type: DamageType = 'poison'): void => {
  e.dotDmg = damage;
  e.dotType = type;
  e.dotEvery = every;
  e.dotTicks = ticks;
  e.lastPid = pid;
  // The wasps carry a conversion's type with them (it goes on when they jump); plain, as they always were.
  e.wasps = type === 'poison' ? { jump } : { jump, dt: type };
};

/** The nearest few enemies to a point (by distance, then id), leaving one out. */
const nearestFew = (s: BattleState, x: number, y: number, r: number, n: number, skip: number): Enemy[] =>
  enemiesNear(s, x, y, r, (o) => o.id !== skip && targetable(s, o))
    .sort((a, b) => dist2(a.x, a.y, x, y) - dist2(b.x, b.y, x, y) || a.id - b.id)
    .slice(0, n);

/** Share of life missing (bp): how badly a hero needs healing. */
export const hurtBp = (h: Hero): number => (h.downTicks > 0 || h.stats.maxHp <= 0 ? 0 : idiv((h.stats.maxHp - h.hp) * 10000, h.stats.maxHp));

/** The most hurt standing hero within reach of a point (the caster counts), or null if nobody is hurt. */
export const mostHurt = (s: BattleState, x: number, y: number, r: number, skip = 0): Hero | null => {
  let best: Hero | null = null;
  let bh = 0;
  for (const o of s.heroes) {
    if (o.id === skip || o.downTicks > 0 || !withinRange(x, y, o.x, o.y, r)) continue;
    const hb = hurtBp(o);
    if (hb > bh) {
      bh = hb;
      best = o;
    }
  }
  return best;
};

/** Overload: the turret blows up, hurting and stunning what stands around it; its fork carries into the blast. */
const overload = (s: BattleState, h: Hero, sk: SkillDef, t: { x: number; y: number }): void => {
  const o = ARTIFICER.overload;
  const dmg = power(h, mulBp(sk.damage, o.damageBp));
  const fork = h.forks?.[0] ?? null;
  for (const e of enemiesNear(s, t.x, t.y, o.radius, (x) => targetable(s, x))) {
    damageEnemy(s, e, dmg, { pid: h.pid });
    const st = stunFor(e, o.stun);
    if (e.stun < st) e.stun = st;
    if (fork === 'frostTurret') {
      const sl = slowFor(e, FORKS.frostTurret.slowBp);
      if (e.slowBp < sl) e.slowBp = sl;
      if (e.slowTicks < FORKS.frostTurret.slowTicks) e.slowTicks = FORKS.frostTurret.slowTicks;
    }
  }
  if (fork === 'fireTurret') {
    const f = FORKS.fireTurret;
    s.zones.push({ id: s.nextId++, kind: 'burn', x: t.x, y: t.y, radius: o.radius, cells: [], ticks: f.ticks, every: f.every, damage: power(h, f.damage), slowBp: 0, pid: h.pid });
  }
  ev(s, { t: 'boom', x: t.x, y: t.y, r: o.radius, kind: fork === 'frostTurret' ? 'overloadFrost' : fork === 'fireTurret' ? 'overloadFire' : 'overload' });
};

/**
 * The artificer's overload, apart from placing turrets: the nearest of its own turrets to the point (or a ready
 * clockwork one within reach) blows up. Its own short cooldown; returns true when something blew up.
 */
export const overloadAt = (s: BattleState, h: Hero, px: number, py: number): boolean => {
  if (h.downTicks > 0 || h.stun > 0 || (h.fear ?? 0) > 0 || (h.sleep ?? 0) > 0 || (h.bear ?? 0) > 0) return false;
  if ((h.overloadCd ?? 0) > 0 || kitOf(h)[0] !== 'turret') return false;
  const sk = ranked(h, 0);
  const o = ARTIFICER.overload;
  let own: Turret | null = null;
  for (const t of s.turrets) {
    if (t.owner !== h.id || t.portable || !withinRange(t.x, t.y, px, py, o.pick)) continue;
    if (!own || dist2(t.x, t.y, px, py) < dist2(own.x, own.y, px, py)) own = t;
  }
  if (own) {
    const id = own.id;
    s.turrets = s.turrets.filter((x) => x.id !== id);
    overload(s, h, sk, own);
  } else {
    // A clockwork turret of the bastion's (era II workshop): it bursts the same way, then rests.
    const gear = s.structures.find((st) => {
      if (st.type !== 'clockwork' || st.destroyed || st.cd > overloadRest(s) - 12) return false;
      const c = hexToWorld(st.q, st.r);
      return withinRange(c.x, c.y, px, py, o.pick) && withinRange(c.x, c.y, h.x, h.y, sk.castRange);
    });
    if (!gear) return false;
    overload(s, h, sk, hexToWorld(gear.q, gear.r));
    gear.cd = overloadRest(s);
  }
  h.overloadCd = o.cooldown;
  h.anim = 'skill0';
  h.animT = 0;
  pstats(s, h.pid).skills++;
  ev(s, { t: 'skill', hero: h.id, skill: 'overload', x: px, y: py });
  return true;
};

/** Tries to cast; returns true on success (cooldown starts). */
/** Throws a nightmare `dist` away from (fx, fy), never into a wall; bosses, the Warden and elites hold their ground. */
export const shove = (s: BattleState, e: Enemy, fx: number, fy: number, dist: number): void => {
  if (stunFor(e, 1) === 0 || e.kind === 'boneWall') return;
  const d = isqrt(dist2(e.x, e.y, fx, fy));
  const to = d > 0 ? { x: e.x + idiv((e.x - fx) * dist, d), y: e.y + idiv((e.y - fy) * dist, d) } : { x: e.x + dist, y: e.y };
  if (!isFlying(e) && !enemySlideClear(s, e.x, e.y, to.x, to.y)) return;
  e.x = to.x;
  e.y = to.y;
  e.next = -1;
};

/** Hex cells in a line across the cast direction, centred on a point (frost wall, thorns, barricade). */
export const wallCells = (s: BattleState, h: Hero, p: { x: number; y: number }, n: number): number[] => {
  const center = worldToHex(p.x, p.y);
  const dir = DIRS[hexDirFor((iatan2(p.y - h.y, p.x - h.x) + 256) % 1024)] as { q: number; r: number };
  const cells: number[] = [];
  const back = idiv(n - 1, 2);
  for (let i = -back; i < n - back; i++) {
    const w = hexToWorld(center.q + dir.q * i, center.r + dir.r * i);
    const id = cellAtWorld(s, w.x, w.y);
    if (id >= 0) cells.push(id);
  }
  return cells;
};

export const slowIt = (e: Enemy, bp: number, ticks: number): void => {
  const sl = slowFor(e, bp);
  if (e.slowBp < sl) e.slowBp = sl;
  if (e.slowTicks < ticks) e.slowTicks = ticks;
};

/** What keeps a standing hero from casting at all right now (the bear only claws), or null. */
const heldBack = (h: Hero): 'stun' | 'fear' | 'sleep' | 'bear' | null =>
  h.stun > 0 ? 'stun' : (h.fear ?? 0) > 0 ? 'fear' : (h.sleep ?? 0) > 0 ? 'sleep' : (h.bear ?? 0) > 0 ? 'bear' : null;

/** Skills that land on a spot and need open ground there (pulled in to their reach first). */
const GROUND_SKILLS: ReadonlySet<SkillId> = new Set<SkillId>(['turret', 'automaton', 'decoy', 'spikeMine', 'teslaCoil', 'snare', 'forestAwakening']);

/**
 * Why a cast at (x, y) would come to nothing now, by the same rules as `castSkill` (which drops it without a
 * word): for the screen to say so instead. Null when it would go (or when only the sim can tell).
 */
export const castBlocked = (s: BattleState, h: Hero, slot: 0 | 1 | 2, x: number, y: number): 'stun' | 'fear' | 'sleep' | 'bear' | 'ground' | null => {
  const held = heldBack(h);
  if (held) return held;
  const sk = ranked(h, slot);
  if (!GROUND_SKILLS.has(sk.id)) return null;
  const p = clampPoint(h, x, y, sk.castRange);
  return heroWalkable(s, cellAtWorld(s, p.x, p.y)) ? null : 'ground';
};

export const castSkill = (s: BattleState, h: Hero, cmd: SkillCmd): boolean => {
  if (h.downTicks > 0 || heldBack(h)) return false;
  const slot = cmd.slot;
  if (h.skillCd[slot] > 0) return false;
  return runCast(s, h, ranked(h, slot), slot, cmd);
};
// The medal's oath goes off by itself (battle/forms.ts): as if its player pressed it, at the warrior.
setCastSlot((s, h, slot) => castSkill(s, h, { t: 'skill', slot }));

/**
 * An echo glyph's second cast (docs/GLIFOS.md §1): the button's skill again where it was aimed, at GLYPH_FX.echo.bp of
 * its power, without a cooldown or an event of its own. A hero held back or down when it comes loses it.
 */
export const echoCast = (s: BattleState, h: Hero): void => {
  const e = h.echo as NonNullable<Hero['echo']>;
  h.echo = undefined;
  if (h.downTicks > 0 || heldBack(h)) return;
  const sk = ranked(h, e.slot);
  const quiet = patched(sk, potency(sk.id, GLYPH_FX.echo.bp));
  ev(s, { t: 'boom', x: h.x, y: h.y, r: 700, kind: 'echo' });
  runCast(s, h, quiet, e.slot, { t: 'skill', slot: e.slot, ...(e.x !== undefined ? { x: e.x } : {}), ...(e.y !== undefined ? { y: e.y } : {}), ...(e.id !== undefined ? { id: e.id } : {}) }, true);
};

/** The whirl: one cut at everything around the barbarian, at `bp` of its damage; returns how many it hit. */
export const whirlPulse = (s: BattleState, h: Hero, bp: number): number => {
  const sk = ranked(h, 0);
  const hits = enemiesNear(s, h.x, h.y, sk.radius, (o) => targetable(s, o));
  // A conversion glyph on the button turns every cut (docs/GLIFOS.md §1).
  const turned = glyphTurns(sk.id, h.glyphs?.[0]);
  for (const e of hits) lifesteal(s, h, damageEnemy(s, e, mulBp(power(h, sk.damage), bp), turned && turned !== 'physical' ? { pid: h.pid, type: turned } : { pid: h.pid }));
  ev(s, { t: 'boom', x: h.x, y: h.y, r: sk.radius, kind: 'whirl' });
  return hits.length;
};

/** Where a leap lands near a point: the point if a hero can stand there, else the nearest neighbour that can. */
export const landing = (s: BattleState, x: number, y: number): { x: number; y: number } | null => {
  if (heroWalkable(s, cellAtWorld(s, x, y))) return { x, y };
  const c = worldToHex(x, y);
  for (const d of DIRS) {
    const p = hexToWorld(c.q + d.q, c.r + d.r);
    if (heroWalkable(s, cellAtWorld(s, p.x, p.y))) return p;
  }
  return null;
};

/** One smite: twice as hard on the corrupt, and it takes a grade of ink. */
export const smiteOne = (s: BattleState, h: Hero, e: Enemy, dmg: number, type: DamageType = 'holy'): number => {
  const bad = corrupt(e);
  const dealt = damageEnemy(s, e, bad ? mulBp(dmg, PALADIN.smite.bonusBp) : dmg, type === 'physical' ? { pid: h.pid, ignoresArmor: true } : { pid: h.pid, ignoresArmor: true, type });
  if (e.tier >= 2 && e.hp > 0) e.tier = (e.tier - 1) as 1 | 2;
  ev(s, { t: 'boom', x: e.x, y: e.y, r: 500, kind: 'smite' });
  return dealt;
};

/** Who or what an aegis goes on: the hero, structure or keep asked for, else whatever stands at the point, else the most hurt hero. */
export const guardTarget = (
  s: BattleState,
  h: Hero,
  cmd: SkillCmd,
  range: number,
): { kind: 'hero' | 'structure' | 'keep'; o: Hero | Structure | null; x: number; y: number } | null => {
  const reach = (x: number, y: number): boolean => withinRange(h.x, h.y, x, y, range);
  if (cmd.keep) return reach(0, 0) ? { kind: 'keep', o: null, x: 0, y: 0 } : null;
  if (cmd.structure) {
    const st = findStructure(s, cmd.structure);
    if (!st || st.destroyed || st.maxHp <= 0) return null;
    const c = structurePos(st);
    return reach(c.x, c.y) ? { kind: 'structure', o: st, x: c.x, y: c.y } : null;
  }
  if (cmd.id) {
    const o = findHero(s, cmd.id);
    if (o && o.downTicks === 0 && reach(o.x, o.y)) return { kind: 'hero', o, x: o.x, y: o.y };
  }
  if (cmd.x !== undefined && cmd.y !== undefined) {
    for (const o of s.heroes) if (o.downTicks === 0 && withinRange(o.x, o.y, cmd.x, cmd.y, 700) && reach(o.x, o.y)) return { kind: 'hero', o, x: o.x, y: o.y };
    const st = structureAtCell(s, cellAtWorld(s, cmd.x, cmd.y));
    if (st && !st.destroyed && st.maxHp > 0) {
      const c = structurePos(st);
      if (reach(c.x, c.y)) return { kind: 'structure', o: st, x: c.x, y: c.y };
    }
    if (s.map.kind === 'bastion' && withinRange(0, 0, cmd.x, cmd.y, 1200) && reach(0, 0)) return { kind: 'keep', o: null, x: 0, y: 0 };
  }
  const o = mostHurt(s, h.x, h.y, range) ?? h;
  return { kind: 'hero', o, x: o.x, y: o.y };
};


export const recordArea = (s: BattleState, h: Hero, id: string, n: number): void => {
  const p = pstats(s, h.pid);
  histAdd((p.areaCounts[id] ??= new Array<number>(10).fill(0)), n, 1);
};

/** A zone's damage type, as damage options (physical adds nothing): a conversion glyph's, else `own`, else its kind's. */
const zt = (z: Zone, own?: DamageType): { type?: DamageType } => {
  const t = z.dt ?? own ?? ZONE_DMG_TYPE[z.kind];
  return t && t !== 'physical' ? { type: t } : {};
};

/**
 * The zones of the learned skills: the frost orb rolls, slows what it touches and bites, then melts (bursting
 * with its variant); the guard totem wards the allies around it (and mends them, or strikes with light); the
 * hammer whirls round its paladin striking everything in reach (stunning every few pulses with its variant).
 */
const learnedZone = (s: BattleState, z: Zone): void => {
  const pulse = z.ticks % z.every === 0;
  if (z.kind === 'mine') {
    // Arming, then the first nightmare on the ground close by sets it off.
    if ((z.warn ?? 0) > 0) {
      z.warn = (z.warn ?? 0) - 1;
      return;
    }
    const near = enemiesNear(s, z.x, z.y, SKILL_FX.spikeMine.trigger, (o) => targetable(s, o) && !isFlying(o));
    if (!near.length) return;
    // The miner's pick (docs/PANOPLIA.md §10): the same burst, and the others near follow.
    if (z.uq) {
      minerBurst(s, z);
      return;
    }
    for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o))) {
      damageEnemy(s, e, z.damage, { pid: z.pid, ...zt(z, 'physical') });
      const sl = slowFor(e, z.slowBp);
      if (e.slowBp < sl) e.slowBp = sl;
      if (e.slowTicks < (z.stun ?? 0)) e.slowTicks = z.stun ?? 0;
    }
    ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'mineBurst' });
    z.ticks = 0;
    return;
  }
  if (z.kind === 'snare') {
    // The first ground nightmare on it is held, hurt (and bled with barbs); the snare is spent. The trapper's jerkin
    // (docs/PANOPLIA.md §10): an elite or a flyer too.
    const g = trapOwner(s, z);
    const e = enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o) && (g ? trapTakes(o) : !isFlying(o)))[0];
    if (!e) return;
    damageEnemy(s, e, z.damage, { pid: z.pid, ...zt(z, 'physical') });
    const st = g ? trapShut(s, g, z, e) : stunFor(e, z.stun ?? 0);
    if (e.stun < st) e.stun = st;
    if (z.bleed) {
      e.dotDmg = z.bleed.damage;
      e.dotType = z.dt ?? 'physical';
      e.dotEvery = z.bleed.every;
      e.dotTicks = z.bleed.ticks;
      e.lastPid = z.pid;
    }
    ev(s, { t: 'boom', x: z.x, y: z.y, r: 500, kind: 'snareShut' });
    z.ticks = 0;
    return;
  }
  if (z.kind === 'fortify') return;
  if (z.kind === 'rift') {
    // Burst, the dream stone's rift stays open (docs/PANOPLIA.md §10).
    if (z.hop && z.uq) {
      riftOpen(s, z);
      return;
    }
    // The rift drags what is near into its middle, then bursts.
    const pull = SKILL_FX.dreamRift.pull;
    for (const e of enemiesNear(s, z.x, z.y, z.radius)) {
      if (e.kind === 'boneWall' || stunFor(e, 1) === 0) continue;
      const d = isqrt(dist2(e.x, e.y, z.x, z.y));
      if (d <= 250) continue;
      const k = d - 250 < pull ? d - 250 : pull;
      const nx = e.x - idiv((e.x - z.x) * k, d);
      const ny = e.y - idiv((e.y - z.y) * k, d);
      if (!isFlying(e)) {
        const to = cellAtWorld(s, nx, ny);
        if (to !== cellAtWorld(s, e.x, e.y) && (to < 0 || cache(s).flow[to] === -1 || blockingAtCell(s, to))) continue;
      }
      e.x = nx;
      e.y = ny;
      e.next = -1;
    }
    if (z.ticks <= 0) {
      const r = SKILL_FX.dreamRift.burst;
      for (const e of enemiesNear(s, z.x, z.y, r, (o) => targetable(s, o))) damageEnemy(s, e, z.damage, { pid: z.pid, ...zt(z, 'shadow') });
      ev(s, { t: 'boom', x: z.x, y: z.y, r, kind: 'riftBurst' });
    }
    return;
  }
  if (z.kind === 'caltrops' || z.kind === 'quake') {
    // Ground that slows (and, caltrops, pricks) the nightmares walking on it.
    for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o) && !isFlying(o))) {
      slowIt(e, z.slowBp, 3);
      if (z.damage > 0 && pulse) damageEnemy(s, e, z.damage, { pid: z.pid, ...zt(z, 'physical') });
    }
    return;
  }
  if (z.kind === 'flare') {
    // Everything under the flare is marked while it burns (and, with the signaler's coat, what flies comes down).
    const g = flareOwner(s, z);
    for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o))) {
      if (g) flareGround(s, g, z, e);
      const bp = SKILL_FX.signalFlare.markBp;
      if (!e.markTicks || e.markTicks <= 2 || (e.markBp ?? 0) <= bp) {
        e.markBp = bp;
        e.markTicks = 3;
      }
    }
    return;
  }
  if (z.kind === 'meteors') {
    // A meteor each pulse, somewhere in the circle (the battle's own dice).
    if (!pulse) return;
    // The comet's ember (docs/PANOPLIA.md §10): its meteors pick.
    if (z.uq) {
      cometMeteor(s, z);
      return;
    }
    const a = rngInt(s.rng, 1024);
    const d = rngInt(s.rng, z.radius + 1);
    const x = z.x + idiv(cosA(a) * d, 10000);
    const y = z.y + idiv(sinA(a) * d, 10000);
    for (const e of enemiesNear(s, x, y, z.blast ?? 900, (o) => targetable(s, o))) damageEnemy(s, e, z.damage, { pid: z.pid, ...zt(z, 'fire') });
    ev(s, { t: 'boom', x, y, r: z.blast ?? 900, kind: 'meteor' });
    return;
  }
  if (z.kind === 'coil') {
    // Each pulse it arcs to the nearest few within reach.
    if (!pulse) return;
    for (const e of nearestFew(s, z.x, z.y, z.radius, z.arcs ?? 1, 0)) {
      damageEnemy(s, e, z.damage, { pid: z.pid, ...zt(z, 'lightning') });
      ev(s, { t: 'beam', x: z.x, y: z.y, tx: e.x, ty: e.y, kind: 'coil' });
    }
    return;
  }
  if (z.kind === 'orb') {
    if (z.steps && z.steps > 0) {
      z.x += z.vx ?? 0;
      z.y += z.vy ?? 0;
      z.steps--;
    }
    // The teacher's robe (docs/PANOPLIA.md §10): there and back.
    if (z.uq) orbTurn(s, z);
    for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o))) {
      if (e.slowBp < slowFor(e, z.slowBp)) e.slowBp = slowFor(e, z.slowBp);
      if (e.slowTicks < 6) e.slowTicks = 6;
      const bit = pulse ? damageEnemy(s, e, z.damage, { pid: z.pid, ...zt(z, 'frost') }) : 0;
      if (z.uq) orbTouched(s, z, e, bit);
    }
    if (z.ticks <= 0 && z.burst) {
      for (const e of enemiesNear(s, z.x, z.y, z.burst.radius, (o) => targetable(s, o))) {
        damageEnemy(s, e, z.burst.damage, { pid: z.pid, ...zt(z, 'frost') });
        // A frost stun is frost: on the dragon of the Burn no dry blow (docs/UNICAS.md §4.3).
        const st = e.kind === 'dragon' && (z.dt ?? 'frost') === 'frost' ? 0 : stunFor(e, z.burst.stun);
        if (e.stun < st) e.stun = st;
      }
      ev(s, { t: 'boom', x: z.x, y: z.y, r: z.burst.radius, kind: 'orbBurst' });
    }
  } else if (z.kind === 'blizzard') {
    // Everything under it, flyers too, is slowed; frost bites every pulse (and freezes every few with its variant).
    const stun = pulse && z.stunEvery && ((z.pulses ?? 0) + 1) % z.stunEvery === 0 ? (z.stun ?? 0) : 0;
    if (pulse) z.pulses = (z.pulses ?? 0) + 1;
    // (The dragon of the Burn in its sky is out of a ground skill's reach: §4.3.)
    for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o) && !(o.kind === 'dragon' && isFlying(o)))) {
      if (e.slowBp < slowFor(e, z.slowBp)) e.slowBp = slowFor(e, z.slowBp);
      if (e.slowTicks < 3) e.slowTicks = 3;
      if (pulse) damageEnemy(s, e, z.damage, { pid: z.pid, ...zt(z, 'frost') });
      if (stun) {
        const st = e.kind === 'dragon' && (z.dt ?? 'frost') === 'frost' ? 0 : stunFor(e, stun);
        if (e.stun < st) e.stun = st;
      }
    }
    if (stun) ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'deepFreeze' });
  } else if (z.kind === 'mortar') {
    // A shell lands each pulse: the first on the point, the next around it.
    if (!pulse) return;
    const k = z.pulses ?? 0;
    z.pulses = k + 1;
    const gap = k === 0 ? 0 : SKILL_FX.fieldMortar.gap;
    const a = (k * 341 + 85) % 1024;
    const x = z.x + idiv(cosA(a) * gap, 10000);
    const y = z.y + idiv(sinA(a) * gap, 10000);
    // The gunner's plate (docs/PANOPLIA.md §10): each shell leaves a crater.
    if (z.uq) mortarShell(s, z, x, y);
    for (const e of enemiesNear(s, x, y, z.radius, (o) => targetable(s, o))) {
      damageEnemy(s, e, z.damage, { pid: z.pid, ...zt(z, 'fire') });
      if (z.stun) {
        const st = stunFor(e, z.stun);
        if (e.stun < st) e.stun = st;
      }
    }
    if (z.burns) {
      const f = FORKS.incendiaryMortar;
      s.zones.push({ id: s.nextId++, kind: 'burn', x, y, radius: f.radius, cells: [], ticks: f.ticks, every: f.every, damage: f.damage, slowBp: 0, pid: z.pid });
    }
    ev(s, { t: 'boom', x, y, r: z.radius, kind: z.stun ? 'mortarHeavy' : 'mortar' });
  } else if (z.kind === 'plague') {
    // The cloud poisons the nightmares in it every pulse (and slows them with its variant).
    for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o))) {
      if (z.slowBp > 0) {
        if (e.slowBp < slowFor(e, z.slowBp)) e.slowBp = slowFor(e, z.slowBp);
        if (e.slowTicks < 3) e.slowTicks = 3;
      }
      if (pulse) poison(e, z.damage, SKILL_FX.plague.every, SKILL_FX.plague.ticks, z.pid);
    }
  } else if (z.kind === 'rampart') {
    // The structures are covered in combat (rampartBp); with its variant, the allies within take less too.
    if (z.wardBp) for (const o of s.heroes) if (o.downTicks === 0 && withinRange(z.x, z.y, o.x, o.y, z.radius)) wardHero(o, z.wardBp, 3);
  } else if (z.kind === 'totem' || z.kind === 'banner') {
    // The gardener's habit (docs/PANOPLIA.md §10): the totem leaps to the most beset ally.
    const g = z.kind === 'totem' ? totemOwner(s, z) : undefined;
    if (g) totemTick(s, g, z);
    for (const o of s.heroes) {
      if (o.downTicks > 0 || !withinRange(z.x, z.y, o.x, o.y, z.radius)) continue;
      if (z.wardBp) wardHero(o, z.wardBp, 3);
      // The guard's banner: nothing stuns or throws them.
      if (z.steady) {
        o.stun = 0;
        o.shove = 0;
      }
      if (pulse && z.heal) healHero(s, o, mulBp(o.stats.maxHp, z.heal), z.pid);
    }
    if (pulse && z.strike) {
      const e = nearestEnemy(s, z.x, z.y, z.radius);
      if (e) {
        damageEnemy(s, e, z.strike, { pid: z.pid, ...zt(z, 'holy') });
        ev(s, { t: 'boom', x: e.x, y: e.y, r: 400, kind: 'totemLight' });
      }
    }
  } else {
    // The whirl turns blows aside from its paladin.
    const o = z.follow ? s.heroes.find((h) => h.id === z.follow) : undefined;
    if (o && o.downTicks === 0) wardHero(o, SKILL_FX.spinHammer.takenBp, 3);
    if (!pulse) return;
    z.pulses = (z.pulses ?? 0) + 1;
    const stun = z.stunEvery && z.pulses % z.stunEvery === 0 ? (z.stun ?? 0) : 0;
    for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o))) {
      damageEnemy(s, e, z.damage, { pid: z.pid, ...zt(z, 'holy') });
      if (stun) {
        const st = stunFor(e, stun);
        if (e.stun < st) e.stun = st;
      }
    }
    ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: stun ? 'hammerStun' : 'hammer' });
  }
};

const LEARNED_ZONES = new Set<Zone['kind']>(['orb', 'totem', 'hammer', 'mine', 'banner', 'blizzard', 'rampart', 'mortar', 'plague', 'coil', 'snare', 'meteors', 'fortify', 'rift', 'caltrops', 'flare', 'quake']);

export const updateZones = (s: BattleState): void => {
  const keep: typeof s.zones = [];
  for (const z of s.zones) {
    z.ticks--;
    if (LEARNED_ZONES.has(z.kind)) learnedZone(s, z);
    else if (z.kind === 'lava' || z.kind === 'eruption') ascuasZone(s, z);
    else if (z.kind === 'wyrmfire' || z.kind === 'nightfall') {
      // A Burn wyrmling's fire on the ground, or the shadow an Unsleeping's ultimate leaves: it hurts the heroes standing in it.
      if (z.ticks % z.every === 0) for (const h of s.heroes) if (h.downTicks === 0 && withinRange(z.x, z.y, h.x, h.y, z.radius)) damageHero(s, h, z.damage, null, z.kind === 'nightfall' ? 'shadow' : 'fire');
    }
    else if (z.kind === 'unBlast') blastZone(s, z);
    // The unique pieces' forms' own ground (docs/UNICAS.md §3).
    else if (FORM_ZONES.has(z.kind)) formZone(s, z);
    else if (z.kind === 'glass' || z.kind === 'shards') obsidianZone(s, z);
    else if (z.kind === 'bolt' || z.kind === 'star') omenZone(s, z);
    else if (z.kind === 'frost' || z.kind === 'thorns' || z.kind === 'barricade') {
      for (const e of s.enemies) {
        if (e.hp <= 0 || isFlying(e)) continue;
        const c = cellAtWorld(s, e.x, e.y);
        if (!z.cells.includes(c)) continue;
        if (e.slowBp < slowFor(e, z.slowBp)) e.slowBp = slowFor(e, z.slowBp);
        if (e.slowTicks < 3) e.slowTicks = 3;
        // Biting frost.
        if (z.damage > 0 && z.ticks % z.every === 0) damageEnemy(s, e, z.damage, { pid: z.pid, ignoresArmor: true, ...zt(z) });
      }
    } else if (z.kind === 'bloom') {
      // The druid's grove: a share of each hero's life every pulse (heal is bp), briars slow and prick.
      if (z.ticks % z.every === 0) {
        let healed = 0;
        for (const o of s.heroes) if (o.downTicks === 0 && withinRange(z.x, z.y, o.x, o.y, z.radius)) healed += healHero(s, o, mulBp(o.stats.maxHp, z.heal ?? 0), z.pid);
        if (z.follow) featZone(s, z, healed, 0);
      }
      if (z.slowBp > 0) {
        for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => !isFlying(o))) {
          if (e.slowBp < slowFor(e, z.slowBp)) e.slowBp = slowFor(e, z.slowBp);
          if (e.slowTicks < 3) e.slowTicks = 3;
        }
      }
      if (z.damage > 0 && z.ticks % z.every === 0) {
        for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o) && !isFlying(o))) damageEnemy(s, e, z.damage, { pid: z.pid, ignoresArmor: true, ...zt(z) });
      }
      // The seed's forms: the tangle, the pollen.
      if (z.fm) bloomTick(s, z);
    } else if (z.kind === 'sanctuary') {
      for (const o of s.heroes) {
        if (o.downTicks > 0 || !withinRange(z.x, z.y, o.x, o.y, z.radius)) continue;
        // Bulwark: standing on it wards.
        if (z.wardBp) wardHero(o, z.wardBp, 3);
        if (z.ticks % z.every === 0) healHero(s, o, z.heal ?? 0, z.pid);
        // The dwarf mail's firm step.
        if (z.fm) sanctuaryHero(z, o);
      }
      // Its atrium.
      if (z.fm) sanctuaryTick(s, z);
      // Consecrated ground burns the nightmares on it.
      if (z.damage > 0 && z.ticks % z.every === 0) {
        for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o) && !isFlying(o))) damageEnemy(s, e, z.damage, { pid: z.pid, ignoresArmor: true, ...zt(z) });
      }
    } else if (z.kind === 'lime') {
      // The lime catapult's cloud (docs/SABERES.md §4.2).
      limeTick(s, z);
    } else if (z.kind === 'storm' || z.kind === 'rain' || z.kind === 'burn') {
      // The whistle's forms: the rain that follows, the spikes.
      if (z.fm && z.kind === 'rain') rainTick(s, z);
      if (z.slowBp > 0) {
        for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => !isFlying(o) || z.kind === 'storm')) {
          if (e.slowBp < slowFor(e, z.slowBp)) e.slowBp = slowFor(e, z.slowBp);
          if (e.slowTicks < 3) e.slowTicks = 3;
        }
      }
      if (z.ticks % z.every === 0) {
        let dealt = 0;
        let reached = 0;
        // The storm's eye stood still: it strikes harder.
        const blow = z.fm && z.kind === 'storm' ? stormBlow(z) : z.damage;
        for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o) && (z.kind !== 'burn' || !isFlying(o)))) {
          dealt += damageEnemy(s, e, blow, { pid: z.pid, ignoresArmor: z.kind !== 'rain', pierce: z.kind === 'rain', ...zt(z) });
          reached++;
          // The closed sky counts the rain's kills.
          if (z.fm && z.kind === 'rain' && e.hp <= 0) rainKilled(s, z, e);
        }
        // The leaping bolt.
        if (z.fm && z.kind === 'storm') stormLeap(s, z, blow);
        if (z.kind !== 'burn') featZone(s, z, dealt, reached);
        if (z.kind !== 'burn') ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: z.kind });
      }
    } else if (z.kind === 'hallow') {
      // Hallowed ground: the inked burn and the heroes on it heal (heal is bp of their life) every pulse;
      // with the weight of faith, all are slowed.
      if (z.slowBp > 0) {
        for (const e of enemiesNear(s, z.x, z.y, z.radius)) {
          if (e.slowBp < slowFor(e, z.slowBp)) e.slowBp = slowFor(e, z.slowBp);
          if (e.slowTicks < 3) e.slowTicks = 3;
        }
      }
      if (z.ticks % z.every === 0) {
        let dealt = 0;
        let inked = 0;
        for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o) && o.tier >= 2)) {
          dealt += damageEnemy(s, e, z.damage, { pid: z.pid, ignoresArmor: true, ...zt(z) });
          inked++;
          // The candle's blinding flame.
          if (z.fm) hallowInked(z, e);
        }
        featZone(s, z, dealt, inked);
        if (z.heal) for (const o of s.heroes) if (o.downTicks === 0 && withinRange(z.x, z.y, o.x, o.y, z.radius)) healHero(s, o, mulBp(o.stats.maxHp, z.heal), z.pid);
      }
    } else if (z.kind === 'spores') {
      // Spores wait for the roots to let go, then slow whoever is in the cloud.
      if (z.warn && z.warn > 0) z.warn--;
      else {
        if (z.warn === 0) {
          z.warn = -1;
          ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'spores' });
        }
        for (const e of enemiesNear(s, z.x, z.y, z.radius)) {
          if (e.slowBp < slowFor(e, z.slowBp)) e.slowBp = slowFor(e, z.slowBp);
          if (e.slowTicks < 3) e.slowTicks = 3;
        }
      }
    } else if (z.kind === 'grenade') {
      const pull = ARTIFICER.grenade.pull;
      for (const e of enemiesNear(s, z.x, z.y, z.radius)) {
        // Bosses, the Warden and elites hold their ground.
        if (e.kind === 'boneWall' || stunFor(e, 1) === 0) continue;
        const d = isqrt(dist2(e.x, e.y, z.x, z.y));
        if (d <= 250) continue;
        const k = d - 250 < pull ? d - 250 : pull;
        const nx = e.x - idiv((e.x - z.x) * k, d);
        const ny = e.y - idiv((e.y - z.y) * k, d);
        // Never dragged through a wall: ground nightmares only slide onto ground they could walk.
        if (!isFlying(e)) {
          const to = cellAtWorld(s, nx, ny);
          if (to !== cellAtWorld(s, e.x, e.y) && (to < 0 || cache(s).flow[to] === -1 || blockingAtCell(s, to))) continue;
        }
        e.x = nx;
        e.y = ny;
        e.next = -1;
      }
      if (z.ticks <= 0) {
        for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o))) {
          damageEnemy(s, e, z.damage, { pid: z.pid, ...zt(z) });
          if (z.slowBp > 0) {
            const sl = slowFor(e, z.slowBp);
            if (e.slowBp < sl) e.slowBp = sl;
            if (e.slowTicks < FORKS.shrapnel.slowTicks) e.slowTicks = FORKS.shrapnel.slowTicks;
          }
        }
        ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: z.slowBp > 0 ? 'grenadeShrapnel' : 'grenade' });
        // Maelis's kit: the blast is also a remedy for the allies it reaches.
        if (z.heal) {
          let healed = 0;
          for (const o of s.heroes) if (o.downTicks === 0 && withinRange(z.x, z.y, o.x, o.y, z.radius)) healed += healHero(s, o, z.heal, z.pid);
          featZone(s, z, healed, 0);
        }
      }
    }
    // The druid's panoply (battle/panoply-druid.ts): the drifting cloud, the holding wall, the contagion, the broken ground.
    if (z.uqz) druidZone(s, z);
    // What the panoply's pieces add to their zones, and their ends (docs/PANOPLIA.md §10).
    if (z.uq) {
      artificerZone(s, z);
      mageZone(s, z);
    }
    if (z.ticks > 0) keep.push(z);
    // What the forms leave when their ground ends (the leaping flame, the vigil, the garden, the puddle): pushed on
    // the ground being walked, so this same loop takes it in.
    else if (z.fm) zoneEnded(s, z);
  }
  s.zones = keep;
};

